import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import Tour from 'reactour';
import { createStructuredSelector } from 'reselect';
import { toggleFeedVisibility } from 'containers/Feed/actions';
import A11yFontIcon from 'components/A11yFontIcon';
import { MENU_LEVEL_FORCED, setMenuOpen } from 'components/Header/actions';
import { selectMenuOpen } from 'components/Header/selectors';
import TextButton from 'components/TextButton';
import withTheme from 'theme/withTheme';
import { clearIntroTour, getIntroTour } from 'utils/localStorage';
import BackIcon from 'resources/Button-Carousel-Back.svg';
import NextIcon from 'resources/Button-Carousel-Next.svg';
import { setTourActive } from './actions';
import { selectTourActive } from './selectors';
import './IntroTour.css';

const INPUT_DELAY_MS = 300;
const STARTING_PAGE = '/';

const NextButtonNew = (
  <TextButton className="next-button">
    <img src={NextIcon} alt="Next Step" />
  </TextButton>
);
const PrevButtonNew = (
  <TextButton className="prev-button">
    <img src={BackIcon} alt="Previous Step" />
  </TextButton>
);

class IntroTour extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      currentStep: 0
    };

    this.handleCloseTour = this.handleCloseTour.bind(this);
    this.handleNextStep = this.handleNextStep.bind(this);
    this.handlePrevStep = this.handlePrevStep.bind(this);

    this.steps = generateSteps(this);
    this.inputDelayTimer = 0;
  }

  componentDidMount() {
    if (getIntroTour()) {
      this.props.setTourActive(true);
    }
  }

  componentWillUnmount() {
    if (this.props.tourActive) {
      this.handleCloseTour();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      !prevProps.tourActive &&
      this.props.tourActive &&
      this.props.location.pathname !== STARTING_PAGE
    ) {
      this.props.redirect(STARTING_PAGE);
    }
  }

  handleCloseTour() {
    this.props.setTourActive(false);
    this.props.setMenuOpen(false);
    this.props.toggleFeedVisibility(false);
    this.setState({ currentStep: 0 });
    clearIntroTour();
  }

  handleNextStep() {
    if (this.state.currentStep < this.steps.length - 1) {
      const newStep = this.state.currentStep + 1;
      this.handleChangeStep(newStep);
    }
  }

  handlePrevStep() {
    if (this.state.currentStep > 0) {
      const newStep = this.state.currentStep - 1;
      this.handleChangeStep(newStep);
    }
  }

  handleChangeStep(newStep) {
    if (Date.now() < this.inputDelayTimer) {
      return;
    }
    this.inputDelayTimer = Date.now() + INPUT_DELAY_MS;

    let setPage = STARTING_PAGE;
    let setMenuOpen = false;
    let toggleFeedVisibility = false;

    const stepData = this.steps[newStep];
    const dispatchData = stepData && stepData.dispatch;
    if (dispatchData) {
      if (dispatchData.page !== undefined) {
        setPage = dispatchData.page;
      }
      if (dispatchData.menuOpen !== undefined) {
        setMenuOpen = dispatchData.menuOpen;
      }
      if (dispatchData.feedOpen !== undefined) {
        toggleFeedVisibility = dispatchData.feedOpen;
      }
    }

    if (this.props.location.pathname !== setPage) {
      this.props.redirect(setPage);
    }

    if (this.props.menuOpen !== setMenuOpen) {
      this.props.setMenuOpen(setMenuOpen);
    }

    if (this.props.feedOpen !== toggleFeedVisibility) {
      this.props.toggleFeedVisibility(toggleFeedVisibility);
    }

    const scrollTop = (time, selector) =>
      new Promise(resolve =>
        setTimeout(() => {
          const element = document.querySelector(selector);
          element.scrollIntoView();
          resolve();
        }, time)
      );

    scrollTop(200, 'body').then(() => {
      this.doChangeStep(newStep);
    });
  }

  doChangeStep(newStep) {
    this.setState({ currentStep: newStep });
  }

  render() {
    return (
      this.props.tourActive && (
        <Tour
          className="intro-tour"
          steps={this.steps}
          isOpen={true}
          onRequestClose={this.handleCloseTour}
          update={this.props.location.pathname}
          updateDelay={500}
          goToStep={this.state.currentStep}
          nextStep={this.handleNextStep}
          prevStep={this.handlePrevStep}
          nextButton={NextButtonNew}
          prevButton={PrevButtonNew}
          showNavigationNumber={false}
          disableDotsNavigation={true}
          disableInteraction={true}
          scrollSmooth={true}
          closeWithMask={false}
          accentColor={this.props.themeColor}
          rounded={20}
          scrollOffset={this.steps[this.state.currentStep]?.scrollOffset}
        />
      )
    );
  }
}

const generateSteps = tour => [
  {
    content: (
      <div className="tour-content">
        <div>
          {'Welcome to WindCreekCasino.com! This tour will quickly walk you '}
          {'through some of the features of this website.'}
        </div>
        <div>
          {'You may also view this tour later by selecting "Site Tour" '}
          {'from the menu.'}
        </div>
        <div className="tour-button">
          <button
            onClick={tour.handleCloseTour}
            type="button"
            className="btn btn-primary"
          >
            Skip Tour
          </button>
        </div>
      </div>
    )
  },
  {
    selector: '.home-page .page-right-panel',
    content:
      'This is the Community Wall. Read messages from the Community Managers and chat with your fellow players!',
    dispatch: { feedOpen: true }
  },
  {
    selector: '.home-page .page-left-panel',
    content:
      'This the home page. Use these buttons to visit the most popular pages on the site.',
    position: 'bottom',
    scrollOffset: -100
  },
  {
    selector: '.home-page .page-left-panel .auctions-container',
    content: (
      <div className="tour-content">
        Visit <strong>Auctions</strong> to bid your WScore on prizes!
      </div>
    ),
    position: 'bottom',
    dispatch: { page: '/auctions' },
    scrollOffset: -100
  },
  {
    selector: '.home-page .page-left-panel',
    content: (
      <div className="tour-content">
        Visit <strong>Games</strong> to play online games for free to earn
        WScore, which can be redeemed in the WStore for great prizes!
      </div>
    ),
    position: 'bottom',
    dispatch: { page: '/slots' },
    scrollOffset: -100
  },
  {
    selector: '.home-page .page-left-panel',
    content: (
      <div className="tour-content">
        Visit <strong>Drawings</strong> to add entries to upcoming online
        drawings for a chance to win prizes!
      </div>
    ),
    position: 'bottom',
    dispatch: { page: '/drawings' },
    scrollOffset: -100
  },
  {
    selector: '.home-page .page-left-panel',
    content: (
      <div className="tour-content">
        <div>
          Visit <strong>Tournaments</strong> to compete in free online
          tournaments against other players to win prizes!
        </div>
        <div>
          When a tournament is live, you can join by going to the In Progress
          tab and pressing PLAY NOW!
        </div>
      </div>
    ),
    position: 'bottom',
    dispatch: { page: '/tournaments' },
    scrollOffset: -100
  },
  /*
  {
    selector: '.home-page .page-left-panel',
    content:
      "Click on Casino Reservations to make a reservation to visit a Wind Creek casino! You don't need a reservation to come play, but having a reservation guarantees you a spot.",
    position: 'bottom',
    dispatch: { page: '/casinoreservation' }
  },
  */
  {
    selector: '.footer .tickets-meter',
    content: (
      <div className="tour-content">
        <div>
          You can add entries into online drawings for various prizes. Each
          drawing allows one free entry, but you can increase your chances with
          additional drawing entries from the WStore, tournaments, and auctions.
        </div>
        <div>
          Make sure to watch the drawing live to claim the prize if one of your
          entries is drawn!
        </div>
      </div>
    ),
    position: 'top'
  },
  {
    selector: '.footer .game-balance-meter',
    content: (
      <div className="tour-content">
        This shows your current credit balance for playing online games.
        Don&apos;t worry, when it runs out you&apos;ll get more credits for
        free!
      </div>
    ),
    position: 'top'
  },
  {
    selector: '.footer .wscore-meter',
    content: (
      <div className="tour-content">
        Win WScore by playing our free online games! Visit the WStore to spend
        your WScore on various perks at our casino locations and online.
      </div>
    ),
    position: 'top',
    dispatch: { page: '/wstore' }
  },
  {
    selector: '.menu-section .My-Rewards',
    content: 'You can also view the rewards available on your rewards account.',
    position: 'bottom',
    dispatch: { menuOpen: true }
  },
  {
    selector: '.header .header-profile-icon',
    content: (
      <div className="tour-content">
        {'Visit your profile to see your club level and other account details.'}
        {' You can also upload a photo to use as your avatar on the site!'}
      </div>
    ),
    position: 'bottom'
  },
  {
    selector: '.header .main-menu .dropdown-menu',
    content: (
      <div className="tour-content">
        <div>
          {'Use the '}
          <A11yFontIcon icon="bars" fixed alt="Main" />
          {' Menu to access the rest of the site, such as the FAQs.'}
        </div>
        <div>
          {
            "The FAQ section contains more information on how this site's various features work."
          }
        </div>
      </div>
    ),
    dispatch: { menuOpen: true }
  },
  {
    selector: '.footer .support-button',
    content: (
      <div className="tour-content">
        <div>
          {'Have more questions? Open the Support Chat and one of our '}
          {'dedicated Community Managers will be happy to help!'}
        </div>
        <div>
          {'You may also view this tour again, by selecting "Site Tour" from '}
          {'the menu.'}
        </div>
        <div className="tour-button">
          <button
            onClick={tour.handleCloseTour}
            type="button"
            className="btn btn-primary"
          >
            {"Got it, let's play!"}
          </button>
        </div>
      </div>
    )
  }
];

const mapStateToProps = createStructuredSelector({
  tourActive: selectTourActive(),
  menuOpen: selectMenuOpen()
});

const mapDispatchToProps = dispatch => ({
  redirect: to => dispatch(push(to)),
  setTourActive: active => dispatch(setTourActive(active)),
  setMenuOpen: open => dispatch(setMenuOpen(open, MENU_LEVEL_FORCED)),
  toggleFeedVisibility: show => dispatch(toggleFeedVisibility(show))
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withRouter(withTheme(withConnect(IntroTour)));
